import { almostZero } from "@xola/ui-kit";
import {
    getPaymentLineItems,
    getPurchaseLineItemsByType,
    isEquipmentItem,
    isMembershipItem,
    isMerchandiseItem,
    isMistPurchase,
    isPurchaseItemCanceled,
    isStripeEnabled,
    isVisibleToSeller,
    parseTime,
    PurchaseItemStatus,
} from "common";
import dayjs from "dayjs";
import { groupBy, minBy, sum, sumBy, uniqBy } from "lodash";
import {
    CouponType,
    DisputeStatus,
    ExperiencePurchaseItem,
    LineItem,
    LineItemObject,
    LineItemSource,
    LineItemStatus,
    LineItemType,
    LineItemVisibility,
    PartnerObject,
    PaymentLineItem,
    PaymentLineItemStatus,
    PaymentMethod,
    PaymentMethodType,
    PaymentReminderStatus,
    PaymentReminderType,
    Product,
    ProductObject,
    Purchase,
    PurchaseItem,
    PurchaseItemObject,
    PurchaseStatus,
    RemoteCard,
    Seller,
    SellerPaymentMethod,
    Tag,
    TagType,
    TicketType,
} from "types";
import { RefundSelectOption } from "../components/Dashboard/RefundOption";
import { CreatePurchaseItemState, CreatePurchaseState } from "../stores/createPurchaseStore";
import { getDefaultPaymentMethod, getGratuityEnabledExperiences } from "./Seller";
import { PaymentFormItem, RefundTerms } from "@/components/PurchaseModification/SplitPayRefundModal";
import { getSeller } from "@/utils/getSeller";

// Can be purchase, waitlist, timeslot, gift etc
export const getCustomerName = (purchase: { customerName: string }) => {
    return purchase.customerName ?? "";
};

// TODO: Just created, not used yet. To do in a separate cleanup branch
export const getPaymentLineItemByMethod = (purchase: Purchase, paymentMethod: string) => {
    return purchase.lineItems.find((lineItem) => {
        return lineItem.type === LineItemType.PAYMENT && lineItem.payment?.method === paymentMethod;
    });
};

export const getItemById = (purchase: Purchase, id: ID) => {
    return purchase.items.find((item) => item.id === id) as ExperiencePurchaseItem;
};

const BNPL_METHODS = new Set<string>([
    PaymentMethod.KLARNA,
    PaymentMethod.AFFIRM,
    PaymentMethod.AFTERPAY,
    PaymentMethod.ZIP,
]);

/**
 * Returns the original payment method from the purchase as-is, including BNPL methods.
 * Use this when you need to identify what method was originally used (e.g. refund flows).
 * For initiating a new payment, use `getInitialPaymentMethod` instead.
 */
export const getOriginalPaymentMethod = (seller: Seller, purchase: Purchase): SellerPaymentMethod => {
    // TODO: Use getPaymentLineItemByMethod
    const cardLineItem = purchase.lineItems.find((lineItem) => {
        return lineItem.type === LineItemType.PAYMENT && lineItem.payment?.method === PaymentMethod.CREDIT_CARD;
    });

    if (cardLineItem) {
        return {
            id: PaymentMethod.CREDIT_CARD,
            name: PaymentMethod.CREDIT_CARD,
            comment: { title: "Comment", enabled: true, required: false },
            card: cardLineItem?.payment?.card,
        };
    }

    const lineItem = purchase.lineItems.find((lineItem) => lineItem.type === LineItemType.PAYMENT);
    const method = lineItem?.payment?.method;

    if (method && method !== PaymentMethod.GIFT && lineItem.coupon?.type !== CouponType.VOUCHER) {
        return {
            id: method,
            name: method,
            comment: { title: "Comment", enabled: true, required: false },
        };
    }

    return getDefaultPaymentMethod(seller);
};

/**
 * Like `getOriginalPaymentMethod` but excludes BNPL methods (Affirm, Klarna, etc.),
 * falling back to the seller default. Use this when initiating a new payment (e.g. Collect Balance).
 */
export const getInitialPaymentMethod = (seller: Seller, purchase: Purchase): SellerPaymentMethod => {
    const original = getOriginalPaymentMethod(seller, purchase);
    if (BNPL_METHODS.has(original.id as string)) {
        return getDefaultPaymentMethod(seller);
    }

    return original;
};

export const hasDeposit = (purchase: Purchase | CreatePurchaseState) => {
    return purchase.paymentReminders?.some((reminder) => reminder.type === PaymentReminderType.SELLER_DEPOSIT);
};

export const getTotalGuests = (purchase: Purchase) => {
    const statuses = new Set([PurchaseItemStatus.CONFIRMED, PurchaseItemStatus.DECLINED, PurchaseItemStatus.CANCELED]);
    const purchaseItems = purchase.items.filter((item: PurchaseItem) => statuses.has(item.status));

    const guests = purchaseItems.every((item) => isExperiencePurchaseItem(item))
        ? uniqBy(
              purchaseItems.flatMap((item) => item.guests || []),
              (guest) => guest?.id,
          ).filter((guest) => guest?.id).length
        : sumBy(purchaseItems, "quantity");

    return guests;
};

export const isCouponApplied = (purchase: Purchase) => {
    return purchase.tags?.some((tag: Tag) => tag.type === TagType.COUPON_CODE);
};

export const hasCouponEligibleItems = (purchase: Purchase) => {
    return purchase.items.some(
        (item) => isExperiencePurchaseItem(item) || isMembershipItem(item) || isMerchandiseItem(item),
    );
};

export const isInvoicePurchase = (purchase: Purchase) => {
    return purchase.paymentReminders?.find((reminder) => {
        return reminder.type === PaymentReminderType.INVOICE_SELLER && reminder.status !== PaymentReminderStatus.IGNORE;
    });
};

export const isEquipmentPurchase = (purchase: Purchase) =>
    purchase.items?.some((item) => isEquipmentItem(item)) ?? false;

export const isPendingPurchase = (purchase: Purchase) => {
    return purchase.items?.some((item) => {
        return [PurchaseItemStatus.PENDING_ACCEPT, PurchaseItemStatus.PENDING_MINIMUM].includes(item.status);
    });
};

export const getCreditCardLineItem = (purchase: Purchase) => {
    // TODO: Use getPaymentLineItemByMethod? This has an additional check by clientSecret
    return purchase.lineItems.find((lineItem) => {
        return lineItem.type === LineItemType.PAYMENT && lineItem.payment?.paymentIntent?.clientSecret;
    });
};

export const getPendingCreditCardLineItem = (purchase: Purchase) => {
    // TODO: This is getPaymentLineItemByMethod and an additional status check
    const pendingLineItems = purchase.lineItems.filter((lineItem) => {
        return (
            lineItem.type === LineItemType.PAYMENT &&
            lineItem?.payment?.method === PaymentMethod.CREDIT_CARD &&
            lineItem.status === LineItemStatus.PENDING
        );
    });

    // Return the last pending line item
    return pendingLineItems.length > 0 ? pendingLineItems[pendingLineItems.length - 1] : null;
};

// Note: This does not check lineItem.includeInAmount (and it should not)
export const getPartnerFeeLineItems = (purchase: Purchase) => {
    const visibilities = new Set([LineItemVisibility.SELLER, LineItemVisibility.ALL]);
    return purchase.lineItems.filter((lineItem) => {
        return lineItem.type === LineItemType.PARTNER_FEE && visibilities.has(lineItem.visibility);
    });
};

// Note: This does not check lineItem.includeInAmount (and it should not)
export const getPartnerFee = (purchase: Purchase, visibility = LineItemVisibility.SELLER, includeAll = true) => {
    const visibilities = new Set([visibility, includeAll ? LineItemVisibility.ALL : undefined].filter(Boolean));
    return purchase.lineItems.find((lineItem) => {
        return lineItem.type === LineItemType.PARTNER_FEE && visibilities.has(lineItem.visibility);
    });
};

export const getPartnerFeeByTraveler = (purchase: Purchase, includeAll = true) => {
    return getPartnerFee(purchase, LineItemVisibility.TRAVELER, includeAll);
};

export const getPartnerFeePaidByTraveler = (purchase: Purchase) => {
    return purchase.lineItems.find((lineItem) => {
        return (
            lineItem.type === LineItemType.PARTNER_FEE &&
            lineItem.source === LineItemSource.TRAVELER &&
            lineItem.includeInAmount
        );
    })?.amount;
};

export const getItemTotal = (purchase: Purchase, item: PurchaseItem) => {
    // TODO: Confirm this -- getPartnerFee already includes things that are visible to seller & all. We can remove second line
    let partnerFee = getPartnerFee(purchase);
    partnerFee = partnerFee && isVisibleToSeller(partnerFee) ? partnerFee : undefined;

    // If this is Mist then the total is just for the amount of the item, otherwise we include partner fee
    return item.amount + (isMistPurchase(purchase) ? 0 : partnerFee?.amount ?? 0);
};

export const getPaymentLineItemsWithoutRefunds = (purchase: Purchase) => {
    return purchase.lineItems.filter((lineItem) => {
        const { type } = lineItem;

        // TODO: We need a method way to check payment + method
        const isPayment = type === LineItemType.PAYMENT && lineItem.payment?.method !== PaymentMethod.LATER;
        const isValid = lineItem.status === PaymentLineItemStatus.COMPLETE;

        return isValid && isPayment;
    });
};

export const getPaymentLineItemsWithRefunds = (purchase: Purchase) => {
    return purchase.lineItems.filter((lineItem) => {
        const isPayment = lineItem.traveler?.id && lineItem.payment?.method !== PaymentMethod.LATER;
        const isValid = lineItem.status === PaymentLineItemStatus.COMPLETE;

        return isValid && isPayment;
    });
};

export const getPaymentFormItems = (
    lineItemsWithoutRefunds: LineItem[],
    lineItemsWithRefunds: LineItem[],
    isAuthorizeNet?: boolean,
): PaymentFormItem[] => {
    const paymentFormItems: PaymentFormItem[] = [];
    const usedRefundIds = new Set<ID>();

    for (const lineItem of lineItemsWithoutRefunds) {
        const { payment } = lineItem;
        const isCreditCard = payment?.method === "cc";

        if (
            (isCreditCard && lineItem.status === LineItemStatus.COMPLETE) ||
            !isCreditCard ||
            (isAuthorizeNet && isCreditCard)
        ) {
            // Only find refund line items that haven't been considered yet
            const refundLineItems = lineItemsWithRefunds.filter(
                (item) =>
                    item.type === LineItemType.REFUND &&
                    item.traveler?.id === lineItem.traveler?.id &&
                    item.payment?.method === lineItem.payment?.method &&
                    item.payment?.card === lineItem.payment?.card &&
                    !usedRefundIds.has(item.id),
            );

            const totalRefund = sum(refundLineItems.map((r) => Math.abs(r.amount)));
            const amount = Math.abs(lineItem.amount) - totalRefund;

            const existingItem = paymentFormItems.find(
                (pfi) =>
                    pfi.customerId === (lineItem.traveler?.id ?? "Other") &&
                    pfi.paidMethod === (lineItem.payment?.method ?? "") &&
                    pfi.card === lineItem.payment?.card,
            );

            if (existingItem) {
                existingItem.amountPaid += amount;
            } else {
                paymentFormItems.push({
                    id: lineItem.id,
                    customerName: "",
                    customerId: lineItem.traveler?.id ?? "Other",
                    amountPaid: amount,
                    paidMethod: payment?.method ?? "",
                    refundTerm: RefundTerms.PARTIAL,
                    refundAmount: 0,
                    refundMethod: payment?.method ?? "",
                    traveler: { id: lineItem.traveler?.id ?? "" },
                    card: payment?.card ?? undefined,
                    last4: payment?.meta?.cardSummary?.last4,
                    paymentMethodType: payment?.meta?.cardSummary?.paymentMethodType,
                });
            }

            // Mark these refund line items as considered
            for (const r of refundLineItems) usedRefundIds.add(r.id);
        }
    }

    return paymentFormItems;
};

export const getTravelerPaymentFormItemCount = (
    purchase: Purchase,
    travelerId: ID,
    isAuthorizeNet?: boolean,
): number => {
    const lineItemsWithoutRefunds = getPaymentLineItemsWithoutRefunds(purchase);
    const lineItemsWithRefunds = getPaymentLineItemsWithRefunds(purchase);
    const paymentFormItems = getPaymentFormItems(lineItemsWithoutRefunds, lineItemsWithRefunds, isAuthorizeNet);

    return paymentFormItems.filter((item) => item.customerId === travelerId).length;
};

export const getAmountForTraveler = (purchase: Purchase, traveler: ID) => {
    const types = new Set([LineItemType.PAYMENT, LineItemType.REFUND]);
    const lineItems = purchase.lineItems.filter((lineItem) => {
        return (
            lineItem.traveler?.id === traveler &&
            types.has(lineItem.type) &&
            lineItem.status === LineItemStatus.COMPLETE
        );
    });
    return sumBy(lineItems, "amount");
};

export const findPaymentReminder = (purchase: Purchase, types: PaymentReminderType[]) => {
    if (!purchase.paymentReminders) {
        return null;
    }

    return purchase.paymentReminders.find((reminder) => {
        return types.includes(reminder.type) && reminder.status !== PaymentReminderStatus.IGNORE;
    });
};

export const isPackage = (product?: Product | PurchaseItem | null) => {
    if (!product) {
        return false;
    }

    return product.object === ProductObject.PACKAGE || isPackageItem(product as PurchaseItem);
};

export const isAnyItemCanceled = (purchase: Purchase) => {
    return purchase.items.some((item) => item.status === PurchaseItemStatus.CANCELED);
};

export const isPackageItem = (purchaseItem?: PurchaseItem | CreatePurchaseItemState) => {
    // FIXIT: api returns nullish 'object' in 'parentProduct'
    // return purchaseItem?.parentProduct?.object === ProductObject.PACKAGE;

    // so for now let's considering that this is an OBJECT by testing only 'parentProduct' exist - not strict check, but like temporary workaround
    return !!purchaseItem?.parentProduct;
};

export const isStoreCredit = (purchaseOrItem?: PurchaseItem | Purchase | null) => {
    if (!purchaseOrItem) {
        return false;
    }

    let item = purchaseOrItem;
    if ("items" in purchaseOrItem) {
        // This is a root level purchase, so just check the first item because store credit purchases
        // have only one item
        item = purchaseOrItem.items[0];
    }

    return item.object === PurchaseItemObject.STORE_CREDIT;
};

export const isGift = (item?: PurchaseItem | CreatePurchaseItemState | null) => {
    return item?.object === PurchaseItemObject.GIFT;
};

export const isExperiencePurchaseItem = (item?: PurchaseItem | null) => {
    return !!item && !isGift(item) && !isStoreCredit(item);
};

export const isStoreCreditWithAddedFunds = (purchase: Purchase) => {
    return (
        isStoreCredit(purchase) &&
        purchase.lineItems.some((lineItem) => {
            return lineItem.object === LineItemObject.PAYMENT && !lineItem.fundingSource;
        })
    );
};

export const getStoreCreditLineItemsForReverseFunding = (purchase: Purchase) => {
    return purchase.lineItems.filter((lineItem) => {
        return lineItem.object === LineItemObject.PAYMENT && lineItem.fundingSource && isVisibleToSeller(lineItem);
    });
};

export const isPending = (item: PurchaseItem) => {
    return [PurchaseItemStatus.PENDING_ACCEPT, PurchaseItemStatus.PENDING_MINIMUM].includes(item.status);
};

export const getLineItemsGroupedByPaymentMethods = (
    purchase: Purchase,
    includeRefund = true,
    includeVoucher = true,
): { [key in PaymentMethod]?: LineItem[] } => {
    const paymentLineItemType = [LineItemType.PAYMENT];
    if (includeRefund) {
        paymentLineItemType.push(LineItemType.REFUND);
    }

    const lineItems = purchase.lineItems.filter((item) => {
        const hasPayment = !!item.payment;
        const isCompleted = item.status === PaymentLineItemStatus.COMPLETE;
        if (!includeVoucher && item.coupon?.type === CouponType.VOUCHER) {
            return false;
        }

        return paymentLineItemType.includes(item.type) && hasPayment && isCompleted;
    });

    return groupBy(lineItems, "payment.method");
};

export const isMultiplePaymentMethods = (purchase: Purchase, includeRefund = true) => {
    let paymentMethodsCount = 0;
    const groupedLineItems = getLineItemsGroupedByPaymentMethods(purchase, includeRefund);
    for (const method of Object.getOwnPropertyNames(groupedLineItems)) {
        const lineItems = groupedLineItems[method as PaymentMethod];
        const total = sumBy(lineItems, "amount");
        if (!almostZero(total)) {
            paymentMethodsCount++;
        }
    }

    return paymentMethodsCount > 1;
};

export const getArrivalOffsetMinutes = (arrivalDate?: string, arrivalTime?: number) => {
    const { hours, minutes } = parseTime(arrivalTime ?? 0);
    return dayjs(arrivalDate).set("hours", hours).set("minute", minutes).diff(dayjs(), "minutes");
};

export const hasBalance = (purchase: Purchase) => {
    return !almostZero(purchase.balance);
};

export const isPurchaseBalanceDue = (purchase: Purchase) => {
    return hasBalance(purchase) && purchase.balance > 0;
};

export const isPurchaseRefundDue = (purchase: Purchase) => {
    return purchase.balance < 0;
};

export const isHold = (purchase: Purchase) => {
    return purchase.status === PurchaseStatus.HOLD;
};

export const isSplitPayPurchase = (purchase: Purchase) => {
    return purchase.splitPayment.enabled;
};

export const getLineItemAmount = (purchase: Purchase, lineItemType: LineItemType) => {
    return purchase.lineItems.find((item) => item.type === lineItemType)?.amount;
};

export const isDisputeSubmitted = (purchase: Purchase) => {
    return purchase.lineItems.some((lineItem) => lineItem.payment?.dispute?.status);
};

export const getDisputedPaymentLineItems = (purchase: Purchase): PaymentLineItem[] => {
    return purchase.lineItems.filter(
        (li): li is PaymentLineItem => li.object === LineItemObject.PAYMENT && !!li.payment?.dispute?.status,
    );
};

export const hasPendingDispute = (purchase: Purchase): boolean => {
    return getDisputedPaymentLineItems(purchase).some((li) => li.payment?.dispute?.status === DisputeStatus.PENDING);
};

export const getPurchasePaymentMethod = (purchase: Purchase): SellerPaymentMethod | null => {
    const lineItems = getPaymentLineItems(purchase);
    const lineItem = lineItems.find((lineItem) => lineItem.payment?.method);
    const method = lineItem?.payment?.method;
    if (method) {
        return {
            id: method,
            name: method,
            comment: { title: "Comment", enabled: true, required: false },
        };
    }

    return null;
};

export const needsPrepare = () => {
    const seller = getSeller();
    return isStripeEnabled(seller);
};

export const getPurchasePendingPaymentMethod = (purchase: Purchase): string | null => {
    const lineItems = getPurchaseLineItemsByType(purchase, LineItemType.PAYMENT);

    const pendingLineItem = lineItems.find((lineItem) => lineItem.status === LineItemStatus.PENDING);
    const method = pendingLineItem?.payment?.method;
    return method ?? null;
};

export const getPurchaseBalanceForRefund = (
    oldPurchase: Purchase,
    newPurchase?: Purchase | null,
    refundOption?: RefundSelectOption | null,
) => {
    if (refundOption === RefundSelectOption.NONE) {
        return 0;
    }

    const newPurchaseBalance = newPurchase?.balance ?? 0;
    if (oldPurchase.balance > 0 && newPurchaseBalance < 0) {
        return newPurchaseBalance;
    }

    if (newPurchaseBalance >= 0) {
        return newPurchaseBalance;
    }

    return newPurchase?.balance ? newPurchase.balance - oldPurchase.balance : oldPurchase.balance;
};

export const shouldEnableDrawerEject = (purchase: Purchase) => {
    const paymentLineItem = getLatestPaymentLineItem(purchase.lineItems);
    const isStoreCreditRefund = paymentLineItem?.refundToStoreCredit;
    // for store credit refund cash drawer should not be ejected
    if (isStoreCreditRefund) {
        return false;
    }

    const paymentMethod = paymentLineItem?.payment?.method;
    return [PaymentMethod.CASH, PaymentMethod.CHECK].includes(paymentMethod as PaymentMethod);
};

export const getLatestPaymentLineItem = (lineItems: LineItem[]): PaymentLineItem | undefined => {
    // Sort the line items by createdAt in descending order to get the latest lineItem first.
    const sortedLineItems = lineItems.sort((a, b) => Number(b.createdAt) - Number(a.createdAt));
    return sortedLineItems.find((lineItem: LineItem) => {
        return lineItem.object === LineItemObject.REFUND || lineItem.object === LineItemObject.PAYMENT;
    }) as PaymentLineItem | undefined;
};

export const isAffiliatePurchase = (purchase: Purchase) => {
    return (
        purchase.items?.some((item) => item.tickets?.some((ticket) => ticket.type === TicketType.AFFILIATE)) ||
        (!!purchase?.partner?.id && purchase?.partner?.object === PartnerObject.AFFILIATE)
    );
};

export const getPendingItemsAmount = (purchase: Purchase) => {
    const pendingItems = purchase.items.filter(({ status }) => {
        return [PurchaseItemStatus.PENDING_ACCEPT, PurchaseItemStatus.PENDING_MINIMUM].includes(status);
    });
    return sumBy(pendingItems, "amount");
};

export const areAllItemsPending = (purchase: Purchase) => {
    return purchase.items.every((item) => isPending(item));
};

export const getDisplayItem = (purchase: Purchase): PurchaseItem => {
    if (isPendingPurchase(purchase)) {
        const pendingItem = purchase.items.find((item) => isPending(item));
        if (pendingItem) {
            return pendingItem;
        }
    }

    if (isPackagePurchase(purchase)) {
        const confirmedItem = purchase.items.find((item) => item.status === PurchaseItemStatus.CONFIRMED);
        if (confirmedItem) {
            return confirmedItem;
        }
    }

    return purchase.items[0];
};

export const getPurchaseProductId = (purchase: Purchase, itemId?: ID) => {
    return (
        purchase.items.find((item) => item.id === itemId)?.product?.id ??
        purchase.items.find((item) => item.parentProduct)?.parentProduct?.id ??
        purchase.items[0].product?.id
    );
};

/**
 * Checks if all payment line items done by the same person using same payment method,
 * for credit card also checks that card is the same.
 * @param purchase
 * @returns {boolean}
 */
export const isOneBuyerWithSamePaymentMethod = (purchase: Purchase): boolean => {
    const lineItems = getPaymentLineItemsWithoutRefunds(purchase);
    const lineItemToCompare = lineItems[0];
    const isCCPaymentMethod = lineItemToCompare?.payment?.method === PaymentMethod.CREDIT_CARD;
    const isSamePayer = lineItems.every((lineItem) => lineItem?.traveler?.id === lineItemToCompare?.traveler?.id);

    if (!isSamePayer) {
        return false;
    }

    if (isCCPaymentMethod) {
        return lineItems.every(
            (lineItem) =>
                lineItem?.payment?.method === PaymentMethod.CREDIT_CARD &&
                lineItem?.payment?.card === lineItemToCompare?.payment?.card,
        );
    }

    return lineItems.every((lineItem) => lineItem?.payment?.method === lineItemToCompare?.payment?.method);
};

export const getMaxOpenCountFromSelectedTimeSlots = (purchase: CreatePurchaseState) => {
    const timeslots = purchase.items?.filter((item) => Boolean(item.timeslot)).map((item) => item.timeslot);
    return minBy(timeslots, (timeslot) => timeslot?.capacity?.open ?? 0)?.capacity?.open;
};

export const getAmountPaidByCard = (purchase: Purchase, card: RemoteCard) => {
    const lineItems = getPaymentLineItems(purchase);
    const ccPaymentLineItems = lineItems.filter(
        (lineItem) => lineItem.payment?.method === PaymentMethod.CREDIT_CARD && lineItem.payment.card === card.id,
    );
    return Math.abs(sumBy(ccPaymentLineItems, "amount"));
};

export const getPartnerPackageItemLineItem = (purchase: Purchase) => {
    return purchase.lineItems.find((lineItem) => lineItem.type === LineItemType.PARTNER_PACKAGE_ITEM);
};

export const getPartnerPurchaseItemLineItems = (purchase: Purchase) => {
    return purchase.lineItems.filter((lineItem) => lineItem.type === LineItemType.PARTNER_PURCHASE_ITEM);
};

export const isPartnerPackageItem = (purchaseItem?: PurchaseItem) => {
    if (purchaseItem?.parentProduct?.object === ProductObject.PARTNER_PACKAGE) {
        return true;
    }
};

export const isPackagePurchase = (purchase: Purchase | CreatePurchaseState) => {
    return !!purchase.items?.[0]?.parentProduct?.id;
};

export const findItemIndex = (
    purchase: Purchase | CreatePurchaseState,
    item: PurchaseItem | CreatePurchaseItemState,
) => {
    return purchase.items.findIndex(({ id }) => id === item.id);
};

export const isPurchaseWithInteracCard = (purchase: Purchase, card?: RemoteCard) => {
    return getPaymentLineItems(purchase).some(
        (lineItem) =>
            lineItem.payment?.meta?.cardSummary?.paymentMethodType === PaymentMethodType.INTERAC &&
            (!card || lineItem.payment.meta.cardSummary.id === card?.id),
    );
};

export const getInteracPaymentLineItem = (purchase: Purchase, card?: RemoteCard) => {
    return getPaymentLineItems(purchase).find(
        (lineItem) =>
            lineItem.payment?.meta?.cardSummary?.paymentMethodType === PaymentMethodType.INTERAC &&
            lineItem.payment.meta.cardSummary.id === card?.id,
    );
};

export const getLineItemById = (purchase: Purchase, id: ID) => {
    return getPaymentLineItems(purchase).find((lineItem) => lineItem.id === id);
};

export const isCommittedPurchase = (purchase?: Purchase) => {
    return purchase?.status === PurchaseStatus.COMMITTED;
};

export const getGratuityEligibleItems = (purchase: Purchase): ExperiencePurchaseItem[] => {
    const gratuityEnabledExperiences = getGratuityEnabledExperiences();
    return purchase.items.filter((item) => {
        if (!isExperiencePurchaseItem(item)) {
            return false;
        }
        if (item.seller?.id !== getSeller().id) {
            return false;
        }
        const isGratuityEnabledForExperience =
            gratuityEnabledExperiences.all || gratuityEnabledExperiences.experiences.includes(item.product.id);
        return !isPurchaseItemCanceled(item) && isGratuityEnabledForExperience;
    }) as ExperiencePurchaseItem[];
};
